/**
 * Single source of truth for LLM and image model metadata.
 * Pricing is verified against Replicate's catalog and locked in models.lock.json.
 * Adding a model: extend the array, add a fixture in src/test/fixtures, ship.
 */

export type LlmPricing = {
  type: 'tokens';
  inputUsdPerMillion: number;
  outputUsdPerMillion: number;
};

export type ImagePricing =
  | { type: 'per-run'; usd: number }
  | { type: 'gpu-seconds'; usdPerSecond: number; expectedSeconds: number };

export type LlmModel = {
  id: string;
  label: string;
  pricing: LlmPricing;
  contextWindow: number;
  supportsJsonMode: boolean;
  supportsVision: boolean;
};

export type ImageCapability = 't2i' | 'edit';

export type ImageModel = {
  id: string;
  label: string;
  capabilities: readonly ImageCapability[];
  pricing: ImagePricing;
  supportedResolutions: readonly number[];
  notes?: string;
};

export const LLM_MODELS = [
  {
    id: 'anthropic/claude-4.5-sonnet',
    label: 'Claude Sonnet 4.5',
    pricing: { type: 'tokens', inputUsdPerMillion: 3, outputUsdPerMillion: 15 },
    contextWindow: 200_000,
    supportsJsonMode: true,
    supportsVision: true,
  },
  {
    id: 'anthropic/claude-opus-4.7',
    label: 'Claude Opus 4.7',
    pricing: { type: 'tokens', inputUsdPerMillion: 15, outputUsdPerMillion: 75 },
    contextWindow: 200_000,
    supportsJsonMode: true,
    supportsVision: true,
  },
  {
    id: 'openai/gpt-5.4',
    label: 'GPT-5.4',
    pricing: { type: 'tokens', inputUsdPerMillion: 5, outputUsdPerMillion: 20 },
    contextWindow: 256_000,
    supportsJsonMode: true,
    supportsVision: true,
  },
] as const satisfies readonly LlmModel[];

export const IMAGE_MODELS = [
  {
    id: 'google/nano-banana-2',
    label: 'Nano Banana 2',
    capabilities: ['t2i', 'edit'],
    pricing: { type: 'per-run', usd: 0.039 },
    supportedResolutions: [1024, 2048, 4096],
    notes: 'Default. Good with brand graphic ads, strong text rendering.',
  },
  {
    id: 'black-forest-labs/flux-1.1-pro',
    label: 'Flux 1.1 Pro',
    capabilities: ['t2i'],
    pricing: { type: 'per-run', usd: 0.04 },
    supportedResolutions: [1024, 2048],
  },
  {
    id: 'ideogram-ai/ideogram-v3',
    label: 'Ideogram v3',
    capabilities: ['t2i'],
    pricing: { type: 'per-run', usd: 0.08 },
    supportedResolutions: [1024, 2048],
    notes: 'Best for typography-heavy designs.',
  },
  {
    id: 'recraft-ai/recraft-v3',
    label: 'Recraft v3',
    capabilities: ['t2i'],
    pricing: { type: 'per-run', usd: 0.04 },
    supportedResolutions: [1024, 2048],
  },
  {
    id: 'bytedance/seedream-4',
    label: 'Seedream 4',
    capabilities: ['t2i', 'edit'],
    pricing: { type: 'per-run', usd: 0.03 },
    supportedResolutions: [1024, 2048],
  },
] as const satisfies readonly ImageModel[];

export type LlmModelId = (typeof LLM_MODELS)[number]['id'];
export type ImageModelId = (typeof IMAGE_MODELS)[number]['id'];

export const DEFAULT_LLM_MODEL: LlmModelId = 'anthropic/claude-4.5-sonnet';
export const DEFAULT_IMAGE_MODEL: ImageModelId = 'google/nano-banana-2';

export function getLlmModel(id: string): LlmModel | undefined {
  return LLM_MODELS.find((m) => m.id === id);
}

export function getImageModel(id: string): ImageModel | undefined {
  return IMAGE_MODELS.find((m) => m.id === id);
}

export function imageModelsSupportingEdit(): readonly ImageModel[] {
  return IMAGE_MODELS.filter((m) => (m.capabilities as readonly ImageCapability[]).includes('edit'));
}

export function imageModelSupportsEdit(id: string): boolean {
  return getImageModel(id)?.capabilities.includes('edit') ?? false;
}
